import React, { Fragment, useState } from "react";
import {
  Box,
  Typography,
  Icon,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import { LedgerFilters } from "./components/LedgerFilters";
import { LedgerSummary } from "./components/LedgerSummary";
import { LedgerTable } from "./components/LedgerTable";
import { JournalEntryDialog } from "./components/JournalEntryDialog";
import { FilterSidebar } from "../../components/FilterSidebar";
import { useBranch } from "../../components/BranchContext";
import { useLedgerData } from "./hooks/useLedgerData";
import { AccountSelector } from "./components/AccountSelector";
import GenerateReportButton from "../../../business-admin/components/generateReports/generateReports";

export const LedgerPage: React.FC = () => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const {
    // State
    selectedAccountId,
    selectedAccount,
    filters,
    page,
    rowsPerPage,
    selectedJournalEntry,
    isJournalDialogOpen,

    // Computed data
    postingAccounts,
    accountSummary,
    tableData,

    // Handlers
    handleAccountChange,
    handleFiltersChange,
    handleResetFilters,
    handleJournalEntryClick,
    handleCloseJournalDialog,
    handleCopyJournalId,
    handlePageChange,
    handlePageSizeChange,
  } = useLedgerData();

  // Sync branch context with local filters when branch changes
  React.useEffect(() => {
    if (filters.selectedBranch !== selectedBranch) {
      handleFiltersChange({ selectedBranch });
    }
  }, [selectedBranch, filters.selectedBranch, handleFiltersChange]);

  // Handle search query changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    handleFiltersChange({ referenceSearch: query });
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "selectedBranch") return false; // Exclude branch as it's handled globally
      if (key === "referenceSearch") return false; // Exclude search as it's handled by search bar
      return value && value.toString().trim() !== "";
    }).length;
  };

  const handleToggleFilterSidebar = () => {
    setIsFilterSidebarOpen(!isFilterSidebarOpen);
  };

  return (
    <Fragment>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
            >
              General Ledger
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontSize: "1rem" }}
            >
              Account-specific transaction history with running balance
            </Typography>
          </Box>
          <Box>{/* <GenerateReportButton /> */}</Box>
        </Box>

        {/* Account Selector & Search */}
        <Box
          sx={{
            mb: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          {/* Account Selector */}
          <Box sx={{ flex: 1, pr: 2 }}>
            <AccountSelector
              selectedAccountId={selectedAccountId}
              onAccountChange={handleAccountChange}
              postingAccounts={postingAccounts}
              selectedAccount={selectedAccount}
            />
          </Box>

          {/* Search & Filters */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search ledger entries..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                minWidth: 280,
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.background.default, 0.7),
                  },
                  "&.Mui-focused": {
                    bgcolor: alpha(theme.palette.background.default, 0.9),
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
                      search
                    </Icon>
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Advanced Filters">
              <IconButton
                onClick={handleToggleFilterSidebar}
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor:
                    getActiveFiltersCount() > 0
                      ? "primary.main"
                      : "transparent",
                  color: getActiveFiltersCount() > 0 ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor:
                      getActiveFiltersCount() > 0
                        ? "primary.dark"
                        : "action.hover",
                  },
                }}
              >
                <Icon>tune</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Account Summary (only if account is selected) */}
        {selectedAccount && (
          <LedgerSummary
            selectedAccount={selectedAccount}
            summary={accountSummary}
          />
        )}

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: "max-content",
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            p: 0,
            position: "relative",
          }}
        >
          {/* 
            TODO: Update LedgerTable component types to properly handle API data format
            Current issue: LedgerTable expects (LedgerEntry & { _id: string })[] but receives LedgerTableEntry[]
            The API returns snake_case with timestamps, UI expects camelCase with formatted dates
            Consider either:
            1. Updating LedgerTable to accept API format directly
            2. Adding proper data transformation layer
            3. Standardizing on one format across the app
          */}
          <LedgerTable
            data={tableData as any} // Temporary type assertion until LedgerTable is updated
            pagination={{
              total: tableData.length,
              page: page,
              pageSize: rowsPerPage,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            onJournalEntryClick={handleJournalEntryClick as any} // Temporary type assertion
            onCopyJournalId={handleCopyJournalId}
          />
        </Box>
      </Box>

      {/* Filter Sidebar */}
      <FilterSidebar
        open={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        title="Ledger Filters"
        activeFiltersCount={getActiveFiltersCount()}
        onResetFilters={handleResetFilters}
        onApplyFilters={() => {
          setIsFilterSidebarOpen(false);
        }}
      >
        <LedgerFilters
          selectedAccountId={selectedAccountId}
          onAccountChange={handleAccountChange}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onResetFilters={handleResetFilters}
          postingAccounts={postingAccounts}
        />
      </FilterSidebar>

      {/* Journal Entry Detail Modal */}
      <JournalEntryDialog
        open={isJournalDialogOpen}
        onClose={handleCloseJournalDialog}
        journalEntry={selectedJournalEntry}
      />
    </Fragment>
  );
};
